/**
 * Unified admin menu configuration - Single source of truth
 * Used by AdminLayout to render the same sidebar across all admin pages.
 *
 * Regla de oro (Carlos feb 2026):
 *   NADA de "si la marca es X, esconder Y". Cada módulo apagado se controla
 *   por su feature flag en `BRAND.features`. Si un ítem no depende de una
 *   flag, va siempre. Si depende, va spread condicional.
 *
 * Los labels del bloque "Tasas de Cambio" se generan a partir de la
 * configuración de marca (BRAND.exchange.primary), para que la instancia
 * de EnviosClick muestre CLP↔PEN sin hardcodeos y GirosBol conserve CLP↔BOB.
 */
import { BRAND } from '../brand';

const feats = BRAND.features || {};

const primary = (BRAND.exchange && BRAND.exchange.primary) || {
  currency: 'BOB',
  flag: '🇧🇴',
};
const cur = primary.currency;
const flag = primary.flag || '🌍';

const exchangeItems = [
  { icon: '📊', label: `CLP → ${cur}`, path: '/admin/exchange' },
  // Corredor inverso: EnviosClick (primary=PEN) usa la vista moderna con
  // 3 componentes + spread por tramos. GirosBol (primary=BOB) sigue con la
  // vista legacy que solo tiene commission_tiers.
  { icon: '🔄', label: `${cur} → CLP`, path: cur === 'PEN' ? '/admin/pen-to-clp' : '/admin/bob-to-clp' },
  { icon: flag, label: `${cur} al Mundo`, path: '/admin/bob-commissions' },
  { icon: '🇨🇱', label: 'CLP al Mundo', path: '/admin/other-countries' },
];

// Bloque Transacciones: sólo mostrar separador si hay al menos un ítem visible.
const transaccionesItems = [
  ...(feats.wallet ? [
    { icon: '💼', label: 'Saldos billeteras', path: '/admin/wallets' },
    { icon: '💰', label: 'Recargas', path: '/admin/recharges' },
    { icon: '💸', label: 'Transferencias', path: '/admin/transfers' },
  ] : []),
  { icon: '📦', label: 'Envíos P2P', path: '/admin/transfer-orders' },
  ...(feats.credibol ? [{ icon: '💳', label: 'CrediBol', path: '/admin/credibol' }] : []),
  ...(feats.sorteo ? [{ icon: '🎰', label: 'Sorteos', path: '/admin/sorteos' }] : []),
  ...((feats.gbPuntos && BRAND.puntosProgramName) ? [{ icon: '⭐', label: BRAND.puntosProgramName, path: '/admin/gb-puntos' }] : []),
];

// Bloque Marketing: WhatsApp Inbox depende del bot. Si no hay bot, no hay inbox.
const marketingItems = [
  ...(feats.whatsappBot ? [{ icon: '📥', label: 'WhatsApp Inbox', path: '/admin/whatsapp-inbox' }] : []),
];

// Bloque Empresas: sólo si la instancia tiene contratos B2B (flag `empresas`).
const empresasHeaderItems = feats.empresas
  ? [{ icon: '🏬', label: 'Empresas', path: '/admin/empresas' }]
  : [];

const adminMenuItems = [
  { icon: '👥', label: 'Usuarios', path: '/admin' },
  { icon: '🧮', label: 'Calculadora', path: '/admin/calculadora' },
  { icon: '➕', label: 'Crear Cliente', path: '/admin/create-user' },
  { icon: '🏢', label: 'Envío en Oficina', path: '/admin/cash-payment' },
  ...empresasHeaderItems,
  ...(transaccionesItems.length > 0
    ? [{ type: 'separator', label: 'Transacciones' }, ...transaccionesItems]
    : []),
  ...(marketingItems.length > 0
    ? [{ type: 'separator', label: 'Marketing' }, ...marketingItems]
    : []),
  { type: 'separator', label: 'Tasas de Cambio' },
  ...exchangeItems,
  { type: 'separator', label: 'Cumplimiento UAF' },
  { icon: '🚨', label: 'Alertas UAF', path: '/admin/uaf/alertas', badgeSource: 'uaf-alerts' },
  { icon: '📥', label: 'Requerimientos UAF', path: '/admin/uaf/requerimientos' },
  { type: 'separator', label: 'Seguridad' },
  { icon: '🚨', label: 'Alertas Fraude', path: '/admin/fraud-alerts' },
  { icon: '📋', label: 'Auditoría', path: '/admin/audit-log' },
  { icon: '🛡️', label: 'Denuncias', path: '/admin/denuncias' },
  { type: 'separator', label: 'Reportes' },
  ...(feats.pitchDeck ? [{ icon: '🎤', label: 'Presentación Socios', path: '/admin/presentacion' }] : []),
  { icon: '📈', label: 'Analíticas', path: '/admin/analytics' },
  { icon: '🌍', label: 'Visitas al sitio', path: '/admin/visitas' },
  { icon: '📊', label: 'Resumen Financiero', path: '/admin/tax' },
  { icon: '📄', label: 'Boletas', path: '/admin/boletas' },
  { icon: '📄', label: 'Reportes', path: '/admin/reports' },
  { icon: '⚙️', label: 'Configuración', path: '/admin/settings' },
  { icon: '🛡', label: 'Crear Admin', path: '/admin/create-admin' },
];

export default adminMenuItems;
